import React, { useState } from "react";

import { Eye, EyeOff } from "lucide-react";

import "../DropdownSelector/style.scss";

type CommonInputProps = {
  label?: string;
  name: string;
  type?: any;
  value: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  readOnly?: boolean;
  onClick?: () => void;
  infoText?: string;
  min?: string;
  max?: string;
  step?: string;
  maxLength?: number;
  lang?: string;
  autoComplete?: string;
  /** `field` matches DropdownSelector field trigger; `legacy` is floating-label. */
  variant?: "legacy" | "field";
};

const CommonInput: React.FC<CommonInputProps> = ({
  label,
  name,
  type = "text",
  value,
  placeholder,
  onChange,
  onKeyDown,
  error,
  required = false,
  disabled = false,
  className = "",
  infoText,
  lang,
  variant = "legacy",
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordType = type === "password";
  const inputType = isPasswordType && showPassword ? "text" : type;
  const isFieldVariant = variant === "field";

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const inputClassName = isFieldVariant
    ? `dropdown-selector-trigger--field dropdown-selector-trigger--field-full cursor-text ${
        error ? "border-red-500" : ""
      } ${disabled ? "dropdown-selector-trigger--field-loading" : ""} ${
        isPasswordType ? "pr-10" : ""
      }`
    : `custom-input__container custom-input__height ${
        error ? "border-red-500" : ""
      } ${isPasswordType ? "pr-10" : ""}`;

  return (
    <div
      className={`${isFieldVariant ? "w-full" : "custom-input relative"} ${className}`}
    >
      {label && (
        <label
          htmlFor={name}
          className={
            isFieldVariant
              ? "mb-1 block text-sm font-medium text-gray-700"
              : "custom-input__label"
          }
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          id={name}
          name={name}
          type={inputType}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onKeyDown={onKeyDown}
          required={required}
          disabled={disabled}
          className={inputClassName}
          lang={lang}
          {...rest}
        />
        {isPasswordType && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
      {infoText && (
        <div className="text-[12px] text-gray-500 mt-1">{infoText}</div>
      )}
      {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
    </div>
  );
};

export default CommonInput;
